<template>
  <div :class="'md-layout'">
    <md-dialog :md-active.sync="isTryingToActivate">
      <md-dialog-title>Activate questionnaire</md-dialog-title>
      <md-dialog-content>
        Are you sure you want to activate the questionnaire?
      </md-dialog-content>
      <md-dialog-actions>
        <md-button :disabled="isSubmitting" @click.prevent="dismissActivateModal">Cancel</md-button>
        <md-button :class="'md-primary'" :disabled="isSubmitting" @click.prevent="activate()">
          {{ !isSubmitting ? "Activate" : "Activating..." }}
        </md-button>
      </md-dialog-actions>
    </md-dialog>

    <div :class="'md-layout-item md-size-100'" v-if="!show.display">
      <md-card>
        <md-card-header :class="'md-card-header-icon md-card-header-danger'">
          <div :class="'card-icon'">
            <md-icon>assignment</md-icon>
          </div>
          <h4 :class="'title'">
            List
            <md-button :class="'md-danger pull-right'" @click.prevent="goToCreate" v-if="cannotView() && questionnaires.length > 0">
              New
            </md-button>
          </h4>
        </md-card-header>
        <md-card-content>
          
          <!--Filters-->
          <div :class="'md-layout'">
            <div :class="'md-layout-item md-xsmall-size-100 md-small-size-50 md-medium-size-33 md-large-size-33'">
              <md-field>
                <label>Name</label>
                <md-input placeholder="Name" v-model="filter.name" @input="getQuestionnaires"/>
              </md-field>
            </div>
            <div :class="'md-layout-item md-xsmall-size-100 md-small-size-50 md-medium-size-20 md-large-size-20'">
              <md-field md-clearable>
                <label>Status</label>
                <md-select @md-selected="getQuestionnaires" placeholder="Status" v-model="filter.status" md-dense>
                  <md-option value="1">Draft</md-option>
                  <md-option value="2">Active</md-option>
                  <md-option value="3">Inactive</md-option>
                </md-select>
              </md-field>
            </div>
          </div>

          <!--Table-->
          <md-table v-model="questionnaires" table-header-color="orange">
            <md-empty-state md-icon="list" md-label="Questionnaires" :md-description=" 'No questionnaires found' +
              (this.filtersQuery ? ' with the searched terms' : '')" v-if="!isFetching">
              <md-button v-if="cannotView()" :class="'md-primary md-raised'" @click.prevent="goToCreate">
                New
              </md-button>
            </md-empty-state>
            <md-table-row slot="md-table-row" slot-scope="{ item: questionnaire }">
              <md-table-cell md-label="Name">{{ questionnaire.name }}</md-table-cell>
              <md-table-cell md-label="RQR Type">{{ questionnaire.rqr_type | rqrType }}</md-table-cell>
              <md-table-cell md-label="Status">{{ questionnaire.status | questionnaireStatus }}</md-table-cell>
              <md-table-cell md-label="Actions" md-numeric>
                <md-button :class="'md-simple md-just-icon'" :md-ripple="false" 
                  @click.prevent="showQuestionnaire(questionnaire)" title="View details">
                  <md-icon>info</md-icon>
                </md-button>
                <md-button v-if="cannotView()" :class="'md-simple md-just-icon'" :md-ripple="false"
                  :disabled="questionnaire.status > 1" @click.prevent=" goToEdit(questionnaire.id, questionnaire.status)"
                  title="Edit questionnaire">
                  <md-icon>edit</md-icon>
                </md-button>
                <md-button v-if="cannotView()" :class="'md-simple md-just-icon'" :md-ripple="false"
                  :disabled="questionnaire.status > 1" @click.prevent="tryToActivate(questionnaire)" 
                  title="Activate questionnaire">
                  <md-icon>check</md-icon>
                </md-button>
              </md-table-cell>
            </md-table-row>
          </md-table>
          <div :class="'md-flex text-center'" v-if="questionnaires.length > 0">
            <Pagination :total="pagination.totalItems" :value="pagination.currentPage" 
              :perPage="pagination.perPage" @input="changePage"/>
          </div>
          <div :class="'md-flex text-center'" v-if="isFetching">
            <md-progress-spinner :class="'md-accent'" :md-stroke="3" md-mode="indeterminate"></md-progress-spinner>
          </div>
        </md-card-content>
      </md-card>
    </div>
    <show-questionnaire v-if="show.display" @close="closeQuestionnaire" :questionnaire="show.questionnaire">
    </show-questionnaire>
  </div>
</template>

<script>
import { SlideYDownTransition } from "vue2-transitions";
import { date, rqrType, questionnaireStatus } from "@/filters";
import { Pagination } from "@/components";
import ShowQuestionnaire from "./Show.vue";
import { camelToSnake } from "@/methods/api/requests";
import * as utils from "@/utils";
import * as filters from "@/filters";

export default {
  created() {
    // Inicializar currentPage com base no parâmetro da rota
    this.pagination.currentPage = parseInt(this.$route.params["id"]) || 1;
    
    this.getQuestionnaires();
  },

  data() {
    return {
      questionnaires: [],
      isFetching: false,
      isSubmitting: false,
      filter: {
        question: "",
        name: "" || this.$route.params.name,
        status: null || this.$route.params.status,
      },
      pagination: {
        totalItems: "",
        currentPage: "",
        perPage: "",
      },
      show: {
        display: false,
        questionnaire: {},
      },
      isTryingToActivate: false,
      activateQuestionnaire: {},
      errorResponses: {
        400: "The users spreadsheet is invalid",
        422: "There is an error with your file",
      },
    };
  },

  components: {
    SlideYDownTransition,
    Pagination,
    ShowQuestionnaire,
  },

  computed: {
    filtersQuery() {
      return filters.getFiltersQuery(this.filter);
    },

    paginationQuery() {
      return utils.getPaginationQuery(this.pagination.currentPage);
    },
  },

  filters: {
    date,
    rqrType,
    questionnaireStatus,
  },

  methods: {
    activate() {
      const questionnaire = this.activateQuestionnaire;
      if (questionnaire.status > 1 || this.isSubmitting)
        return;

      this.isSubmitting = true;
      this.$api.request
        .put(`questionnaires/${questionnaire.id}/activate`)
        .then(() => {
          this.$toast.show("Questionnaire successfully activated");
          this.getQuestionnaires();
          this.dismissActivateModal();
        })
        .catch((err) => this.$toast.show(err.response.data.message))
        .finally(() => (this.isSubmitting = false));
    },

    cannotView() {
      return utils.getCannotView(this.$store.state.currentProfile)
    },

    changePage(toPage) {
      if (toPage === this.pagination.currentPage)
        return;      

      this.pagination.currentPage = toPage;
      this.$router.push({ name: "Questionnaires", params: { id: toPage } });
      this.getQuestionnaires();
    },

    closeQuestionnaire() {
      this.show.questionnaire = {};
      this.show.display = false;
    },

    dismissActivateModal() {
      this.isTryingToActivate = false;
      this.activateQuestionnaire = {};
    },

    getQuestionnaires() {
      this.isFetching = true;
      const page = this.pagination.currentPage || this.$route.params["id"] || 1;
      this.$api
        .request(
          `questionnaires?${this.filtersQuery}page=${page}`
        )
        .then((response) => {
          this.questionnaires = camelToSnake(response.data.data);
          this.pagination.totalItems = response.data.meta.total;
          this.pagination.currentPage = response.data.meta.currentPage;
          this.pagination.perPage = response.data.meta.perPage;
        })
        .finally(() => {
          this.isFetching = false;
        });
    },

    goToCreate() {
      this.$router.push({ name: "Create Questionnaire" });
    },

    goToEdit(id, status) {
      if (status > 1)
        return;
      
      this.$router.push({ name: "Edit Questionnaire", params: { id, actualPage: this.$route.params["id"], name: this.filter.name, status: this.filter.status } });
    },

    showQuestionnaire(questionnaire) {
      this.$api
        .request(`questionnaires/${questionnaire.id}`)
        .then((response) => {
          this.show.questionnaire = camelToSnake(response.data);
          this.show.display = true;
        })
        .catch((err) => {
          this.$toast.show(err.response.data.message);
        });
    },

    tryToActivate(questionnaire) {
      this.isTryingToActivate = true;
      this.activateQuestionnaire = questionnaire;
    },
  },
};
</script>
